import { db, doc, setDoc, serverTimestamp } from "./firebase";
import { sanitizeFirestoreData, getSafeSolicitationDocId, ensureFirestoreSafeSize } from "./utils/firestoreSanitizer";
// @ts-ignore
import csvContent from "../Stateprocsuburls.csv?raw";

export async function seedData(userUid?: string): Promise<{
  statesSeeded: number;
  solicitationsSeeded: number;
  contractsSeeded: number;
  vendorProfileSeeded: boolean;
}> {
  try {
    const response = await fetch("/api/seed-data");
    if (!response.ok) {
      throw new Error(`Failed to fetch seed data: ${response.status}`);
    }
    const rawData = (await response.json()) as any;
    const statesData = rawData.public?.states || {};
    const exportedVendor = rawData.vendorProfile || {};
    
    // Parse Stateprocsuburls.csv
    const importSourceMap: { [key: string]: string } = {};
    if (csvContent) {
      const csvLines = csvContent.split("\n");
      for (const line of csvLines) {
        if (!line.trim()) continue;
        const parts = line.split(",");
        if (parts.length >= 3) {
          const sCode = parts[1].trim();
          let sUrl = parts.slice(2).join(",").trim();
          sUrl = sUrl.replace(/^["']|["']$/g, "").replace(/""/g, '"');
          if (sCode && sCode !== "StateCode") {
            importSourceMap[sCode] = sUrl;
          }
        }
      }
    }
    
    let statesSeededCount = 0;
    let solicitationsSeededCount = 0;
    let vendorProfileSeeded = false;

    const stateMap: { [key: string]: string } = {
      "Alabama": "AL",
      "Alaska": "AK",
      "Arizona": "AZ",
      "Arkansas": "AR",
      "California": "CA",
      "Colorado": "CO",
      "Connecticut": "CT",
      "Delaware": "DE",
      "Florida": "FL",
      "Georgia": "GA",
      "Hawaii": "HI",
      "Idaho": "ID",
      "Illinois": "IL",
      "Indiana": "IN",
      "Iowa": "IA",
      "Kansas": "KS",
      "Kentucky": "KY",
      "Louisiana": "LA",
      "Maine": "ME",
      "Maryland": "MD",
      "Massachusetts": "MA",
      "Michigan": "MI",
      "Minnesota": "MN",
      "Mississippi": "MS",
      "Missouri": "MO",
      "Montana": "MT",
      "Nebraska": "NE",
      "Nevada": "NV",
      "New Hampshire": "NH",
      "New Jersey": "NJ",
      "New Mexico": "NM",
      "New York": "NY",
      "North Carolina": "NC",
      "North Dakota": "ND",
      "Ohio": "OH",
      "Oklahoma": "OK",
      "Oregon": "OR",
      "Pennsylvania": "PA",
      "Rhode Island": "RI",
      "South Carolina": "SC",
      "South Dakota": "SD",
      "Tennessee": "TN",
      "Texas": "TX",
      "Utah": "UT",
      "Vermont": "VT",
      "Virginia": "VA",
      "Washington": "WA",
      "West Virginia": "WV",
      "Wisconsin": "WI",
      "Wyoming": "WY"
    };

    // 1. Seed Current User's Vendor Profile (if logged in)
    if (userUid) {
      const { getDoc } = await import("firebase/firestore");
      const docRef = doc(db, "vendors", userUid);
      const docSnap = await getDoc(docRef);
      const existingData = docSnap.exists() ? docSnap.data() : null;

      const addressString = exportedVendor.primaryAddress 
        ? `${exportedVendor.primaryAddress.addressLine1 || ""}, ${exportedVendor.primaryAddress.city || ""}, ${exportedVendor.primaryAddress.state || ""} ${exportedVendor.primaryAddress.zipCode || ""}`
        : "123 Quantum Way, Suite 100";

      // Preserve existing user fields to prevent them from being wiped out on re-seeding
      const defaultCapabilities: string[] = [];
      if (exportedVendor.supplierCertifications) {
        if (exportedVendor.supplierCertifications.dbe) defaultCapabilities.push("(DBE) Disadvantaged Business Enterprise");
        if (exportedVendor.supplierCertifications.mbe) defaultCapabilities.push("(MBE) Minority Business Enterprise");
        if (exportedVendor.supplierCertifications.wbe) defaultCapabilities.push("(WBE) Women Business Enterprise");
        if (exportedVendor.supplierCertifications.sba) defaultCapabilities.push("(SDB) Small Disadvantaged Business");
      }
      if (defaultCapabilities.length === 0) {
        defaultCapabilities.push("(MBE) Minority Business Enterprise", "(SDB) Small Disadvantaged Business");
      }

      const finalCapabilities = (existingData && existingData.capabilities)
        ? existingData.capabilities
        : defaultCapabilities;

      const finalDesignations = (existingData && existingData.designations)
        ? existingData.designations
        : finalCapabilities;

      const finalTaxClassification = (existingData && existingData.taxClassification)
        ? existingData.taxClassification
        : (exportedVendor.corporationType || "");

      const vendorRecord = {
        // Form & App expected fields
        uid: userUid,
        companyName: existingData?.companyName || exportedVendor.businessName || "Quantum AI Solutions Inc.",
        ein: existingData?.ein || exportedVendor.ein || "12-3456789",
        address: existingData?.address || addressString,
        contactEmail: existingData?.contactEmail || exportedVendor.primaryContactEmail || exportedVendor.businessContactEmail || "isa.hunter@quantumaiinc.solutions",
        capabilities: finalCapabilities,
        designations: finalDesignations,
        taxClassification: finalTaxClassification,
        naicsCodes: existingData?.naicsCodes || exportedVendor.naicsCodes || ["541511", "541512", "92962"],
        createdAt: existingData?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),

        // Map and preserve all original export schemas
        accountNumber: existingData?.accountNumber || exportedVendor.accountNumber || "",
        bankName: existingData?.bankName || exportedVendor.bankName || "",
        billToAddress: existingData?.billToAddress || exportedVendor.billToAddress || null,
        businessContactEmail: existingData?.businessContactEmail || exportedVendor.businessContactEmail || "",
        businessContactExt: existingData?.businessContactExt || exportedVendor.businessContactExt || "",
        businessContactFax: existingData?.businessContactFax || exportedVendor.businessContactFax || "",
        businessContactTelephone: existingData?.businessContactTelephone || exportedVendor.businessContactTelephone || "",
        businessName: existingData?.businessName || exportedVendor.businessName || "",
        businessType: existingData?.businessType || exportedVendor.businessType || "",
        corporationType: existingData?.corporationType || exportedVendor.corporationType || "",
        disabled: existingData?.disabled || exportedVendor.disabled || "",
        duns: existingData?.duns || exportedVendor.duns || "",
        ethnicity: existingData?.ethnicity || exportedVendor.ethnicity || [],
        ethnicityOther: existingData?.ethnicityOther || exportedVendor.ethnicityOther || "",
        foreignCountry: existingData?.foreignCountry || exportedVendor.foreignCountry || "",
        genderIdentity: existingData?.genderIdentity || exportedVendor.genderIdentity || [],
        genderIdentityOther: existingData?.genderIdentityOther || exportedVendor.genderIdentityOther || "",
        invoiceAddress: existingData?.invoiceAddress || exportedVendor.invoiceAddress || null,
        lgbtqia: existingData?.lgbtqia || exportedVendor.lgbtqia || [],
        lgbtqiaOther: existingData?.lgbtqiaOther || exportedVendor.lgbtqiaOther || "",
        owners: existingData?.owners || exportedVendor.owners || [],
        primaryAddress: existingData?.primaryAddress || exportedVendor.primaryAddress || null,
        primaryContactEmail: existingData?.primaryContactEmail || exportedVendor.primaryContactEmail || "",
        primaryContactExt: existingData?.primaryContactExt || exportedVendor.primaryContactExt || "",
        primaryContactFax: existingData?.primaryContactFax || exportedVendor.primaryContactFax || "",
        primaryContactFirstName: existingData?.primaryContactFirstName || exportedVendor.primaryContactFirstName || "",
        primaryContactLastName: existingData?.primaryContactLastName || exportedVendor.primaryContactLastName || "",
        primaryContactMobile: existingData?.primaryContactMobile || exportedVendor.primaryContactMobile || "",
        primaryContactTelephone: existingData?.primaryContactTelephone || exportedVendor.primaryContactTelephone || "",
        primaryContactTitle: existingData?.primaryContactTitle || exportedVendor.primaryContactTitle || "",
        primaryContactType: existingData?.primaryContactType || exportedVendor.primaryContactType || "",
        raceOther: existingData?.raceOther || exportedVendor.raceOther || "",
        routingNumber: existingData?.routingNumber || exportedVendor.routingNumber || "",
        shipToAddress: existingData?.shipToAddress || exportedVendor.shipToAddress || null,
        supplierCertifications: existingData?.supplierCertifications || exportedVendor.supplierCertifications || null,
        website: existingData?.website || exportedVendor.website || ""
      };

      await setDoc(docRef, vendorRecord, { merge: true });
      vendorProfileSeeded = true;
      console.log(`Seeded vendor profile for user ${userUid}`);
    }

    // 2. Loop through all 52 states in the export file to seed State Profiles & Selected RFX Items
    const stateEntries = Object.entries(statesData) as [string, any][];
    for (const [stateName, stateDetails] of stateEntries) {
      if (!stateDetails) continue;
      const stateAbbr = stateMap[stateName] || stateName.slice(0, 2).toUpperCase();
      const sp = stateDetails.stateprofile;

      // Map and build State Profile record
      if (sp) {
        const stateProfileRecord = {
          // React UI / Service expected fields
          state: sp.name || stateName,
          stateCode: stateAbbr,
          procurementUrl: sp.stateprocpubsolurl || sp.vendorUrl || sp.loginUrl || "https://purchasing.alabama.gov/",
          registrationUrl: sp.vendorUrl || sp.loginUrl || "https://vendors.alabama.gov/",
          keyRegulations: sp.rosp ? JSON.stringify(sp.rosp) : "State bid laws and public works procurement regulations.",
          preferencePrograms: Array.isArray(sp.doingBusiness)
            ? sp.doingBusiness.map((item: any) => item.heading || "").filter(Boolean)
            : (sp.doingBusiness ? Object.keys(sp.doingBusiness) : ["Small Business Preferred", "MWBE"]),
          thresholds: {
            informalThreshold: "$15,000",
            formalThreshold: "$100,000"
          },
          naspoParticipation: true,
          summary: sp.overview ? sp.overview.replace(/<[^>]*>/g, "").slice(0, 300).trim() + "..." : `Comprehensive procurement laws and oversight regulations for the State of ${stateName}.`,
          
          // Original Export Fields (fully kept)
          doingBusiness: sp.doingBusiness || null,
          flagUrl: sp.flagUrl || "",
          isRegistered: sp.isRegistered !== undefined ? sp.isRegistered : true,
          loginUrl: sp.loginUrl || "",
          name: sp.name || stateName,
          overview: sp.overview || "",
          password: sp.password || "",
          rosp: sp.rosp || null,
          stateID: sp.stateID || "",
          stateprocpubcontractsurl: sp.stateprocpubcontractsurl || "",
          stateprocpubsolurl: sp.stateprocpubsolurl || "",
          team: sp.team || [],
          username: sp.username || "",
          vendorId: sp.vendorId || "",
          vendorUrl: sp.vendorUrl || "",
          
          lastUpdated: serverTimestamp()
        };

        await setDoc(doc(db, "state_profiles", stateAbbr), stateProfileRecord);
        statesSeededCount++;
      }

      // 3. Seed RFXs (solicitations and nested item details)
      const rfxList = stateDetails.rfxs ? (Object.entries(stateDetails.rfxs) as [string, any][]) : [];
      let stateRfxSeeded = 0;

      for (const [rfxId, rfx] of rfxList) {
        if (!rfx) continue;

        // Limit the number of RFXs per state to keep the DB load perfect,
        // but always include any entry that has rich `details` (like SRC0000028839)
        const hasRichDetails = !!rfx.details;
        if (!hasRichDetails && stateRfxSeeded >= 2) {
          continue; // Cap at 2 generic solicitations per state
        }

        // Determine correct status
        // Ensure the rich Harley Davidson solicitation and some others are active/open
        let mappedStatus: "open" | "closed" | "awarded" = "closed";
        if (hasRichDetails || (rfx.status && rfx.status.toLowerCase().includes("open")) || rfx.participate) {
          mappedStatus = "open";
        }

        // Convert dueclosedate ("MM/DD/YYYY") to ISO string
        let isoDueDate = new Date(Date.now() + 45 * 24 * 3600 * 1000).toISOString(); // Default future
        if (rfx.dueclosedate) {
          const parsed = Date.parse(rfx.dueclosedate);
          if (!isNaN(parsed)) {
            // If the date has passed but the solicitation should be open/interactable, let's keep a current or future year
            const d = new Date(parsed);
            if (d < new Date()) {
              d.setFullYear(new Date().getFullYear() + 1); // push to next year as a convenience
            }
            isoDueDate = d.toISOString();
          }
        }

        const stateImportSource = importSourceMap[stateAbbr] || `https://procure.${stateAbbr.toLowerCase()}.gov`;

        const solicitationRecord = {
          // App standard expected fields
          solicitationId: rfx.sourcingprojectnumber || rfxId,
          title: rfx.solicitationlabel || "Procurement opportunity specification",
          state: stateAbbr,
          agency: rfx.buyingagency || `${stateName} State Portal`,
          description: rfx.maincommodity || "Procurement solicitation item and commodities.",
          dueDate: isoDueDate,
          status: mappedStatus,
          sourceUrl: rfx.rfxitempageurl || "https://example.gov",
          category: rfx.maincommodity || rfx.solicitationtype || "Procurement Service",
          createdAt: serverTimestamp(),
          importSource: stateImportSource,

          // Preserve original RFX export properties
          buyingagency: rfx.buyingagency || "",
          dueclosedate: rfx.dueclosedate || "",
          maincommodity: rfx.maincommodity || "",
          participate: rfx.participate !== undefined ? rfx.participate : false,
          remainingtime: rfx.remainingtime || "",
          rfxitempageurl: rfx.rfxitempageurl || "",
          solicitationlabel: rfx.solicitationlabel || "",
          solicitationtype: rfx.solicitationtype || "",
          sourcingprojectnumber: rfx.sourcingprojectnumber || "",
          sourcingresponsiblefirstname: rfx.sourcingresponsiblefirstname || "",
          sourcingresponsiblelastname: rfx.sourcingresponsiblelastname || "",
          rawStatus: rfx.status || "",

          // RFX Item Detail maps inside this document structure for optimal retrieval
          details: rfx.details ? {
            importSource: stateImportSource,
            generalinformation: rfx.details.generalinformation || null,
            response: rfx.details.response ? {
              ...rfx.details.response,
              rows: Array.isArray(rfx.details.response.rows) ? rfx.details.response.rows.map((r: any) => {
                if (Array.isArray(r)) {
                  const obj: Record<string, string> = {};
                  r.forEach((val, i) => {
                    obj[String(i)] = val;
                  });
                  return obj;
                }
                return r;
              }) : (rfx.details.response.rows || null)
            } : null,
            solicitationdocuments: rfx.details.solicitationdocuments || []
          } : null
        };

        await setDoc(doc(db, "solicitations", getSafeSolicitationDocId(solicitationRecord.solicitationId)), ensureFirestoreSafeSize(solicitationRecord));
        solicitationsSeededCount++;
        stateRfxSeeded++;
      }
    }

    // 3. Seed Contracts Database
    const contractsToSeed = [
      {
        contractId: "CON-2025-CO-801",
        title: "Enterprise AI Modernization & Cloud Solutions",
        state: "CO",
        agency: "Colorado Department of State (CDOS)",
        awardee: "Quantum AI Solutions Inc.",
        awardDate: "2025-01-15",
        effectiveDate: "2025-02-01",
        expirationDate: "2028-02-01",
        status: "Active",
        value: 2450000,
        naics: "541511",
        description: "Modernization of corporate filing workflows, integrating secure AI-powered document verification, and continuous Google Workspace synchronization.",
        winningProposalExcerpt: "Our technical methodology leverages real-time semantic caching and state-level business registry grounding, guaranteeing 99.9% accurate business address matching against Secretary of State filings."
      },
      {
        contractId: "CON-2025-TX-914",
        title: "Statewide Integrated Security Assessment & Audit Services",
        state: "TX",
        agency: "Texas Department of Public Safety (DPS)",
        awardee: "Sentinel Cybersecurity Group",
        awardDate: "2025-03-10",
        effectiveDate: "2025-04-01",
        expirationDate: "2027-04-01",
        status: "Active",
        value: 1850000,
        naics: "541512",
        description: "Biennial state security evaluations, penetration testing of primary agency portals, and comprehensive compliance auditing against NIST controls.",
        winningProposalExcerpt: "By deploying lightweight passive scanning clusters combined with automated NIST 800-53 cross-correlation tables, Sentinel guarantees zero operational latency during compliance checks."
      },
      {
        contractId: "CON-2024-FL-103",
        title: "Emergency Communications Network & Tower Maintenance",
        state: "FL",
        agency: "Florida Division of Emergency Management",
        awardee: "Atlantic Telecom Infrastructure LLC",
        awardDate: "2024-09-01",
        effectiveDate: "2024-10-01",
        expirationDate: "2029-10-01",
        status: "Active",
        value: 8400000,
        naics: "238210",
        description: "Continuous operational support and physical/electrical reinforcement of emergency communication towers, ensuring hurricane readiness and cellular fallback redundancy.",
        winningProposalExcerpt: "Atlantic leverages a pre-staged depot model with dual-team dispatch algorithms, guaranteeing field restoration within 4 hours of physical tower disconnect."
      },
      {
        contractId: "CON-2025-CA-774",
        title: "Renewable Energy Grid Integration & Software Analytics",
        state: "CA",
        agency: "California Energy Commission",
        awardee: "Apex Grid Solutions LLC",
        awardDate: "2025-02-18",
        effectiveDate: "2025-03-01",
        expirationDate: "2028-03-01",
        status: "Active",
        value: 4920000,
        naics: "541511",
        description: "Development of cloud-native scheduling and analytics software to track renewable energy generation peaks and predict grid battery bank dispatch times.",
        winningProposalExcerpt: "Our system implements predictive load modeling using low-parameter neural engines on localized sensor inputs, reducing solar curtailment waste by an estimated 14%."
      },
      {
        contractId: "CON-2025-NY-415",
        title: "Municipal Water Treatment Facility Filtration Upgrade",
        state: "NY",
        agency: "New York Department of Environmental Protection",
        awardee: "Empire Water Technologies Corp",
        awardDate: "2025-01-20",
        effectiveDate: "2025-02-15",
        expirationDate: "2026-08-15",
        status: "Active",
        value: 3600000,
        naics: "237110",
        description: "Subcontractor and equipment delivery for secondary carbon-filtration loops, improving organic compound removal metrics across 14 rural reservoir checkpoints.",
        winningProposalExcerpt: "Empire utilizes an engineered modular design, enabling standard field crews to execute sub-assembly swaps under 6 hours without triggering plant shutdowns."
      },
      {
        contractId: "CON-2025-NC-302",
        title: "High-Performance Compute Node Delivery & Installation",
        state: "NC",
        agency: "North Carolina State University",
        awardee: "Quantum AI Solutions Inc.",
        awardDate: "2025-04-05",
        effectiveDate: "2025-04-15",
        expirationDate: "2026-04-15",
        status: "Active",
        value: 950000,
        naics: "541519",
        description: "Delivery, installation, and software compilation/optimization of liquid-cooled high-performance servers to support machine learning workloads.",
        winningProposalExcerpt: "Quantum's deep-engineering expertise with standard Slurm scheduling configurations and direct liquid cooling integration ensures 2.4x greater density in standard racks."
      }
    ];

    for (const contract of contractsToSeed) {
      await setDoc(doc(db, "contracts", contract.contractId), contract);
    }

    console.log(`Successfully completed import: ${statesSeededCount} states, ${solicitationsSeededCount} solicitations, ${contractsToSeed.length} contracts.`);
    return {
      statesSeeded: statesSeededCount,
      solicitationsSeeded: solicitationsSeededCount,
      contractsSeeded: contractsToSeed.length,
      vendorProfileSeeded
    };

  } catch (error) {
    console.error("Critical error seeding from export file:", error);
    throw error;
  }
}
